import React, { useEffect, useMemo, useState } from 'react';
import { serverInfo } from '../shared/config/serverConfig';
import ErrorBanner from '../components/ErrorBanner';
import ConfirmationModal from '../components/ConfirmationModal';
import VisibilityReorderPicker from '../components/VisibilityReorderPicker';


import { apiFetch } from "../shared/api/apiFetch";

const serverUrl = serverInfo.serverUrl;

// Reusable form for both Create and Edit
function RoleForm({
  mode,
  roleId,
  onRoleIdChange,
  description,
  onDescriptionChange,
  assignedIds,
  onAssignedIdsChange,
  permissions,
  onSubmit,
  onCancel,
  submitting,
  submitLabel,
}) {
  const permissionItems = useMemo(
    () =>
      (permissions || []).map((permission) => ({
        id: permission._id,
        label: permission.description || permission._id,
      })),
    [permissions]
  );

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      <h3 id="role-form-modal-title" style={{ marginTop: 0 }}>{mode === 'create' ? 'Create new role' : 'Edit role'}</h3>
      <div style={{ marginBottom: 16, width: '100%', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <label style={{ flex: '0 0 auto' }}>Role ID</label>
        <input
          type="text"
          value={roleId}
          onChange={(e) => onRoleIdChange && onRoleIdChange(e.target.value)}
          placeholder="e.g. Manager"
          style={{ width: 140, padding: 6, boxSizing: 'border-box' }}
          readOnly={mode === 'edit' && !onRoleIdChange}
        />
        <label style={{ flex: '0 0 auto', marginLeft: 8 }}>Description</label>
        <input
          type="text"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder="e.g. Can manage projects and events"
          style={{ flex: '1 1 200px', minWidth: 200, padding: 6, boxSizing: 'border-box' }}
        />
      </div>
      <div style={{ marginBottom: 12, width: '100%' }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Permissions</label>
        <VisibilityReorderPicker
          items={permissionItems}
          selectedIds={assignedIds}
          onChangeSelectedIds={(ids) => onAssignedIdsChange([...ids].sort())}
          minSelected={0}
          enableReorder={false}
          searchPlaceholder="Search permissions…"
          emptyMessage="No permissions match your search."
        />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function RolePermissionAdmin() {
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [draftPerms, setDraftPerms] = useState({});
  const [formMode, setFormMode] = useState(null);
  const [editingOriginalRoleId, setEditingOriginalRoleId] = useState(null);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editingDescription, setEditingDescription] = useState('');
  const [newRoleId, setNewRoleId] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [newRolePerms, setNewRolePerms] = useState([]);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    mode: 'confirm',
    onConfirm: null,
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiFetch(serverUrl + '/permissions').then((r) => r.json()),
      apiFetch(serverUrl + '/roles').then((r) => r.json()),
    ])
      .then(([permList, roleList]) => {
        if (cancelled) return;
        setPermissions(Array.isArray(permList) ? permList : []);
        const list = Array.isArray(roleList) ? roleList : [];
        setRoles(list);
        const draft = {};
        list.forEach((r) => {
          draft[r._id] = (r.permission_ids || []).slice();
        });
        setDraftPerms(draft);
      })
      .catch((err) => {
        if (!cancelled) setError(err && err.message ? err.message : 'Failed to load roles and permissions.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const saveRole = (originalId, roleId, description) => {
    const permission_ids = (draftPerms[originalId] || []).slice();
    const payload = { description, permission_ids };
    if ((roleId || '').trim() !== originalId) payload._id = (roleId || '').trim();
    setSavingId(originalId);
    setError('');
    const effectiveId = payload._id !== undefined ? (payload._id || '').trim() : originalId;
    if (!effectiveId) {
      setError('Role ID is required.');
      return;
    }
    if (payload._id !== undefined && payload._id === 'Super Admin') {
      setError('"Super Admin" is a reserved role.');
      return;
    }
    apiFetch(serverUrl + '/roles/' + encodeURIComponent(originalId), {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((d) => Promise.reject(new Error(d.message || 'Save failed')));
        return res.json();
      })
      .then((updated) => {
        setDraftPerms((prev) => {
          const next = { ...prev };
          delete next[originalId];
          next[updated._id] = (updated.permission_ids || []).slice();
          return next;
        });
        setRoles((prev) => {
          const withoutOld = prev.filter((r) => r._id !== originalId);
          const existing = withoutOld.find((r) => r._id === updated._id);
          const list = existing
            ? withoutOld.map((r) => (r._id === updated._id ? { ...r, description: updated.description } : r))
            : [...withoutOld, updated];
          return list.sort((a, b) => (a._id < b._id ? -1 : 1));
        });
        setFormMode(null);
        setEditingOriginalRoleId(null);
        setEditingRoleId(null);
      })
      .catch((err) => setError(err && err.message ? err.message : 'Failed to save role.'))
      .finally(() => setSavingId(null));
  };

  const createRole = () => {
    const id = (newRoleId || '').trim();
    const description = (newRoleDescription || '').trim();
    if (!id) { setError('Role ID is required.'); return; }
    if (!description) { setError('Description is required.'); return; }
    if (id === 'Super Admin') { setError('"Super Admin" is a reserved role.'); return; }
    setError('');
    setCreating(true);
    apiFetch(serverUrl + '/roles', {
      method: 'POST',
      body: JSON.stringify({ _id: id, description, permission_ids: newRolePerms }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((d) => Promise.reject(new Error(d.message || 'Create failed')));
        return res.json();
      })
      .then((created) => {
        setRoles((prev) => [...prev, created].sort((a, b) => (a._id < b._id ? -1 : 1)));
        setDraftPerms((prev) => ({ ...prev, [created._id]: (created.permission_ids || []).slice() }));
        setFormMode(null);
        setNewRoleId('');
        setNewRoleDescription('');
        setNewRolePerms([]);
      })
      .catch((err) => setError(err && err.message ? err.message : 'Failed to create role.'))
      .finally(() => setCreating(false));
  };

  const openCreateForm = () => {
    setFormMode('create');
    setNewRoleId('');
    setNewRoleDescription('');
    setNewRolePerms([]);
    setError('');
  };

  const openEditForm = (role) => {
    setFormMode('edit');
    setEditingOriginalRoleId(role._id);
    setEditingRoleId(role._id);
    setEditingDescription(role.description || '');
    setError('');
  };

  const openDuplicateForm = (role) => {
    const existingIds = new Set(roles.map((r) => r._id));
    let suggestedId = `${role._id} Copy`;
    let n = 1;
    while (existingIds.has(suggestedId)) {
      n += 1;
      suggestedId = `${role._id} Copy ${n}`;
    }
    setFormMode('create');
    setNewRoleId(suggestedId);
    setNewRoleDescription(role.description ? `Copy of ${role.description}` : `Copy of ${role._id}`);
    setNewRolePerms((draftPerms[role._id] || role.permission_ids || []).slice());
    setError('');
  };

  const deleteRole = (role) => {
    setConfirmDialog({
      open: true,
      title: 'Delete role',
      message: `Delete role "${role._id}"? Users with this role will have it removed from their account.`,
      mode: 'confirm',
      onConfirm: () => {
        setDeletingId(role._id);
        setError('');
        apiFetch(serverUrl + '/roles/' + encodeURIComponent(role._id), { method: 'DELETE' })
          .then((res) => {
            if (!res.ok) return res.json().then((d) => Promise.reject(new Error(d.message || 'Delete failed')));
            return res.json();
          })
          .then(() => {
            setRoles((prev) => prev.filter((r) => r._id !== role._id));
            setDraftPerms((prev) => {
              const next = { ...prev };
              delete next[role._id];
              return next;
            });
          })
          .catch((err) => setError(err && err.message ? err.message : 'Failed to delete role.'))
          .finally(() => setDeletingId(null));
      },
    });
  };

  const closeForm = () => {
    setFormMode(null);
    setEditingOriginalRoleId(null);
    setEditingRoleId(null);
    setNewRoleId('');
    setNewRoleDescription('');
    setNewRolePerms([]);
    setError('');
  };

  const handleCreateSubmit = () => {
    createRole();
  };

  if (loading) return <div style={{ padding: 16 }}>Loading roles and permissions…</div>;

  const editableRoles = roles.filter((r) => r._id !== 'Super Admin');
  const permById = {};
  permissions.forEach((p) => { permById[p._id] = p; });

  return (
    <div style={{ padding: 16, maxWidth: 1200 }}>
      <ConfirmationModal
        open={confirmDialog.open}
        title={confirmDialog.title || 'Confirm'}
        message={confirmDialog.message}
        mode={confirmDialog.mode || 'confirm'}
        onConfirm={async () => {
          const fn = confirmDialog.onConfirm;
          setConfirmDialog({ open: false, title: '', message: '', mode: 'confirm', onConfirm: null });
          if (fn) await fn();
        }}
        onCancel={() => setConfirmDialog({ open: false, title: '', message: '', mode: 'confirm', onConfirm: null })}
      />
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      <h1>Roles & Permissions</h1>
      <p style={{ color: '#555', marginBottom: 24 }}>
        Control which menus each role can access. <strong>Super Admin</strong> always has full access and is not listed here.
      </p>

      {(formMode === 'create' || (formMode === 'edit' && editingOriginalRoleId)) && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && closeForm()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="role-form-modal-title"
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            {formMode === 'create' && (
              <RoleForm
                mode="create"
                roleId={newRoleId}
                onRoleIdChange={setNewRoleId}
                description={newRoleDescription}
                onDescriptionChange={setNewRoleDescription}
                assignedIds={newRolePerms}
                onAssignedIdsChange={setNewRolePerms}
                permissions={permissions}
                onSubmit={handleCreateSubmit}
                onCancel={closeForm}
                submitting={creating}
                submitLabel="Create role"
              />
            )}
            {formMode === 'edit' && editingOriginalRoleId && (
              <RoleForm
                mode="edit"
                roleId={editingRoleId}
                onRoleIdChange={setEditingRoleId}
                description={editingDescription}
                onDescriptionChange={setEditingDescription}
                assignedIds={draftPerms[editingOriginalRoleId] || []}
                onAssignedIdsChange={(ids) =>
                  setDraftPerms((prev) => ({ ...prev, [editingOriginalRoleId]: ids }))
                }
                permissions={permissions}
                onSubmit={() => saveRole(editingOriginalRoleId, editingRoleId, editingDescription)}
                onCancel={closeForm}
                submitting={savingId === editingOriginalRoleId}
                submitLabel="Save"
              />
            )}
          </div>
        </div>
      )}

      {!formMode && (
        <div style={{ marginBottom: 16 }}>
          <button type="button" onClick={openCreateForm}>Create new role</button>
        </div>
      )}

      {editableRoles.length === 0 && !formMode && (
        <p>No editable roles found. Run the seed script: <code>node server/scripts/seed-permissions.js</code></p>
      )}

      {editableRoles.length > 0 && (
        <div className="cards-wrapper cards-wrapper--roles">
          {editableRoles.map((role) => {
            const assignedIds = draftPerms[role._id] || [];
            const assignedLabels = assignedIds.map((id) => permById[id]).filter(Boolean).map((p) => p.description || p._id);
            return (
              <div key={role._id} className="card card--muted card--no-accent">
                <div className="card-header" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <div className="card-header-content" style={{ alignItems: 'baseline', gap: 4, flexWrap: 'nowrap', lineHeight: 1.25, minWidth: 0 }}>
                    <strong className="card-title" style={{ color: '#111', fontSize: 13 }}>{role._id}</strong>
                    <span className="card-meta-muted" style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: '100%' }} title={role.description || undefined}>— {role.description || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => openDuplicateForm(role)}
                      style={{
                        padding: '3px 8px',
                        fontSize: 11,
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        background: '#f9fafb',
                        cursor: 'pointer',
                      }}
                    >
                      Duplicate
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteRole(role)}
                      disabled={deletingId === role._id}
                      style={{
                        padding: '3px 8px',
                        fontSize: 11,
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        background: deletingId === role._id ? '#f3f4f6' : '#fef2f2',
                        color: deletingId === role._id ? '#9ca3af' : '#b91c1c',
                        cursor: deletingId === role._id ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {deletingId === role._id ? 'Deleting…' : 'Delete'}
                    </button>
                    <button
                      type="button"
                      onClick={() => openEditForm(role)}
                      style={{
                        padding: '3px 8px',
                        fontSize: 11,
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        background: '#f9fafb',
                        cursor: 'pointer',
                      }}
                    >
                      Edit
                    </button>
                  </div>
                </div>
                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <ul style={{
                    margin: 0,
                    paddingLeft: 14,
                    fontSize: 11,
                    height: '100%',
                    overflowY: 'auto',
                    lineHeight: 1.35,
                    boxSizing: 'border-box',
                    columnCount: 2,
                    columnGap: 10,
                  }}>
                    {assignedLabels.length === 0 ? (
                      <li style={{ color: '#9ca3af', breakInside: 'avoid' }}>None</li>
                    ) : (
                      assignedLabels.sort().map((label) => (
                        <li key={label} style={{ breakInside: 'avoid' }}>{label}</li>
                      ))
                    )}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
